import { Play } from "lucide-react";
import { fileUrl } from "../lib/api";
import { SectionTitle } from "./VIP";

// Universal multi-platform video resolver. Auto-detects platform from URL so legacy
// (YouTube-only) records still embed even without an explicit `platform` field.
export function videoEmbed(url, platform) {
  if (!url) return null;
  const p = (platform || "").toLowerCase();
  if (p.includes("direct") || /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url)) return { type: "file", src: url };

  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (m || p.includes("youtube")) return { type: "iframe", src: m ? `https://www.youtube.com/embed/${m[1]}` : url };

  m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m || p.includes("vimeo")) return { type: "iframe", src: m ? `https://player.vimeo.com/video/${m[1]}` : url };

  if (p.includes("facebook") || url.includes("facebook.com") || url.includes("fb.watch"))
    return { type: "iframe", src: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false` };

  if (p.includes("instagram") || url.includes("instagram.com")) {
    const c = url.split("?")[0].replace(/\/+$/, "");
    return { type: "iframe", src: `${c}/embed` };
  }

  if (p.includes("tiktok") || url.includes("tiktok.com")) {
    const tm = url.match(/video\/(\d+)/);
    if (tm) return { type: "iframe", src: `https://www.tiktok.com/embed/v2/${tm[1]}` };
    return { type: "link", src: url };
  }
  return { type: "iframe", src: url };
}

export function VideoGrid({ items, title = "Videos", kicker = "Watch" }) {
  const list = Array.isArray(items) ? items.filter((v) => v && v.video_url) : [];
  if (!list.length) return null;
  return (
    <div data-testid="video-grid">
      <SectionTitle kicker={kicker} title={title} />
      <div className="grid sm:grid-cols-2 gap-6 mt-6">
        {list.map((vd, i) => {
          const emb = videoEmbed(vd.video_url, vd.platform);
          return (
            <div key={i} data-testid={`video-${i}`}>
              <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black">
                {emb?.type === "file" ? (
                  <video src={fileUrl(emb.src)} controls poster={vd.thumbnail ? fileUrl(vd.thumbnail) : undefined} className="w-full h-full object-contain bg-black" />
                ) : emb?.type === "link" ? (
                  <a href={emb.src} target="_blank" rel="noreferrer" className="relative w-full h-full flex items-center justify-center group">
                    {vd.thumbnail ? <img src={fileUrl(vd.thumbnail)} alt={vd.title || ""} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-white/5" />}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/30 transition-colors"><Play className="text-white" fill="white" size={40} /></div>
                  </a>
                ) : (
                  <iframe title={vd.title || `video-${i}`} src={emb?.src} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
                )}
              </div>
              {(vd.title || vd.platform) && <p className="text-sm mt-3 font-medium">{vd.title}{vd.platform ? <span className="text-zinc-500 font-normal"> · {vd.platform}</span> : ""}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
